/**
 * The client's Sprint calendar (LG-412) -- the week rail the LG-1000 prototype
 * settled on, built against `dashboard.sprints.calendar`.
 *
 * A row of the Sprints the client holds sits above one continuous column of
 * weeks, with a single teal line drawn across the column at the last week
 * those Sprints reach. Skipping a week drops it out of the walk and the line
 * steps down -- the authored moment of the whole surface.
 *
 * Three things the rail has to solve beyond that:
 *
 * 1. Weeks that already happened are not the client's question. They live
 *    behind a Historical tab; Active opens first and starts at this week.
 * 2. The generated names are handles, not descriptions. Each week leads with
 *    its dates; the name sits under them as subtext, with a tooltip saying what
 *    it is and where it came from.
 * 3. Nothing sets a Sprint's focus today, so the row says nothing about the
 *    week's content. Whether they are in the week is a single teal check at
 *    its head; the right side carries the one thing worth acting on -- when
 *    the week locks while it is still ahead, and the skip where a skip is
 *    legal. (The homework link and the finished tally the prototype showed on
 *    running and past weeks wait on LG-1073, which gives the payload a way to
 *    reach a week's homework.)
 *
 * The server decides the reach and whether a move is legal; this file only
 * renders those answers. See `calendar-model.ts` for what it derives itself.
 */
import { TRPCClientError } from '@trpc/client'
import {
  CheckIcon,
  InfoIcon,
  Loader2Icon,
  LockIcon,
  UndoIcon,
} from 'lucide-react'
import {
  type ComponentProps,
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import {
  formatLockDay,
  formatLockDayShort,
  formatMonth,
  formatWeek,
  isJoined,
  isRunningLow,
  lastCoveredWeekId,
  missedLockIn,
  monthKey,
  participationLabel,
  rondalWeeks,
  type SprintCalendar,
  type SprintCalendarWeek,
  splitWeeks,
  sprintsLeftLabel,
  weekNote,
  weekPhase,
} from './calendar-model'

const NAMING_BLURB =
  'Every week is given a random two-word name when it is created — a handle you and your coach can say out loud instead of "the week of the 21st". It says nothing about the work.'

/** How long a row stays lit after a rondal card lands on it. */
const FLASH_MS = 1600

/**
 * Buying more Sprints is LG-927 and has no destination yet. The button stays
 * where it will live -- always in the same place, under the balance note --
 * so the buy path never moves when it opens. Disabled rather than hidden, and
 * with no hover title: a disabled button never fires one on a touch device
 * anyway, so the note's own sentence is what carries the explanation.
 */
function BuyButton({
  children,
  className,
  size,
}: {
  children: string
  className?: string
  size?: 'sm' | 'default'
}) {
  return (
    <Button disabled size={size} className={cn('cursor-default', className)}>
      {children}
    </Button>
  )
}

/**
 * A sentence behind a tap. Radix's tooltip never opens from a touch and a
 * span trigger is unreachable from the keyboard, so every hint on this page
 * is a popover on a real button: click, tap and Enter all open it.
 */
function Hint({
  hint,
  className,
  children,
  ...props
}: {
  hint: ReactNode
  className?: string
  children: ReactNode
} & Pick<ComponentProps<'button'>, 'aria-label'>) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={className} {...props}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-xs p-3 text-sm">
        {hint}
      </PopoverContent>
    </Popover>
  )
}

function SprintName({ week }: { week: SprintCalendarWeek }) {
  return (
    <Hint
      hint={<>This week's name. {NAMING_BLURB}</>}
      className="text-muted-foreground hover:text-foreground decoration-muted-foreground/40 cursor-help text-xs underline decoration-dotted underline-offset-4 transition-colors"
    >
      {week.name}
    </Hint>
  )
}

/**
 * The right-hand side: one thing per week, chosen by where the week sits.
 * The lock-in moment for everything still ahead; a word for the week underway
 * that locked before the client could be in it; nothing for a week behind
 * them until LG-1073 gives it a homework link.
 */
function WeekAction({ week }: { week: SprintCalendarWeek }) {
  const phase = weekPhase(week)
  if (week.sprintStatus === 'suspended') return null
  if (phase === 'ended' || phase === 'running') {
    if (missedLockIn(week, phase)) {
      // Everyone joins mid-week, and almost nobody makes that week's lock-in.
      return (
        <p className="text-muted-foreground max-w-56 text-xs">
          Sprint locked before you joined the program — sit tight and you are in
          the next one.
        </p>
      )
    }
    return null
  }

  const joined = isJoined(week)
  const skipped = week.status === 'skipped'
  const locked = phase === 'locking'
  // A skipped row keeps its lock line: the row stays the same height as its
  // neighbours, and the time still matters -- it is the deadline to unskip.
  return (
    <Hint
      hint={
        locked
          ? skipped
            ? 'This week has locked in. You skipped it, so no Sprint was spent.'
            : joined
              ? 'This week has locked in. The Sprint is spent and your homework publishes Monday.'
              : 'This week has locked in without you. The next one is still open.'
          : skipped
            ? 'You skipped this week. Unskip it before that moment and your Sprints reach it again.'
            : joined
              ? 'Until that moment you can still skip this week. After it, the Sprint is spent and the homework publishes Monday.'
              : 'This week locks in then. Buy the Sprints to reach it and you are in it.'
      }
      className={cn(
        'inline-flex cursor-help items-center gap-1.5 text-xs',
        joined ? 'text-muted-foreground' : 'text-muted-foreground/70',
      )}
    >
      <LockIcon className="size-3" />
      {/* The full line needs a real column; a phone gets the day and the verb. */}
      <span className="hidden sm:inline">
        {locked ? 'Locked at' : 'Locks'} {formatLockDay(week)}, noon UTC
        {skipped && week.canUnskip && '. Unskip before then to join it'}
      </span>
      <span className="sm:hidden">
        {locked ? 'Locked' : 'Locks'} {formatLockDayShort(week)}
        {skipped && week.canUnskip && '. Unskip to join'}
      </span>
    </Hint>
  )
}

/**
 * In the Sprint or not, in one glyph. Teal for the weeks their Sprints reach --
 * the Book Teal rule keeps it a stroke, never a filled disc. The client is a
 * participant in a week, never an owner of it; the copy says so.
 */
function ParticipationMark({ week }: { week: SprintCalendarWeek }) {
  const joined = isJoined(week)
  const label = participationLabel(week, weekPhase(week))
  return (
    <Hint
      hint={label}
      aria-label={label}
      className={cn(
        'mt-0.5 flex size-5 shrink-0 cursor-help items-center justify-center rounded-full border',
        joined
          ? 'border-[#51B0BC]/60 text-[#51B0BC]'
          : 'border-border border-dashed',
      )}
    >
      {joined && <CheckIcon className="size-3" strokeWidth={3} />}
    </Hint>
  )
}

/**
 * The Sprints the client holds, as cards across the top: the week they are in
 * now, every week ahead their Sprints reach, and the ones they skipped, greyed
 * the way the row below is. Clicking one jumps to that week. It is one row at
 * every width -- it scrolls sideways rather than wrapping, so the block above
 * the list never changes height.
 */
function SprintRondal({
  weeks,
  onJump,
}: {
  weeks: readonly SprintCalendarWeek[]
  onJump: (sprintId: string) => void
}) {
  const cards = rondalWeeks(weeks)

  if (cards.length === 0) {
    return (
      <div className="border-border rounded-lg border border-dashed p-4">
        <p className="text-sm font-medium">No Sprints yet</p>
        <p className="text-muted-foreground mt-1 text-sm">
          Buy your first and it books itself onto the next week that has not
          locked — every week below opens up from there.
        </p>
      </div>
    )
  }

  return (
    <div className="-mx-1 flex snap-x snap-mandatory flex-nowrap items-stretch gap-2 overflow-x-auto px-1 pb-2">
      {cards.map((week) => {
        const current = weekPhase(week) === 'running'
        const skipped = week.status === 'skipped'
        return (
          <button
            key={`card-${week.sprintId}-${week.status}`}
            type="button"
            onClick={() => onJump(week.sprintId)}
            aria-label={`Jump to ${formatWeek(week)}, ${week.name}`}
            className={cn(
              'sprint-token flex w-40 shrink-0 snap-start flex-col rounded-lg border p-2.5 text-left transition-colors',
              current && 'border-[#51B0BC] bg-[#51B0BC]/25',
              !current &&
                !skipped &&
                'border-[#51B0BC]/50 bg-[#51B0BC]/10 hover:border-[#51B0BC]',
              skipped && 'border-border bg-muted/40 border-dashed',
            )}
          >
            <span
              className={cn(
                'block text-sm font-medium tabular-nums',
                skipped && 'text-muted-foreground line-through',
              )}
            >
              {formatWeek(week)}
            </span>
            <span className="text-muted-foreground block truncate text-xs">
              {week.name}
            </span>
            {/* Always a third line, so a skip never resizes the row. */}
            <span className="text-muted-foreground mt-auto block pt-1 text-xs font-medium">
              {current ? 'This week' : skipped ? 'Skipped' : 'Booked'}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function WeekRow({
  ref,
  week,
  flashed,
  pending,
  moveError,
  onSkip,
  onUnskip,
}: {
  ref: (node: HTMLDivElement | null) => void
  week: SprintCalendarWeek
  flashed: boolean
  pending: boolean
  moveError: string | null
  onSkip: () => void
  onUnskip: () => void
}) {
  const phase = weekPhase(week)
  const skipped = week.status === 'skipped'
  const suspended = week.sprintStatus === 'suspended'
  const past = phase === 'ended'
  const note = weekNote(week, phase)

  return (
    <div
      ref={ref}
      id={`week-${week.sprintId}`}
      className={cn(
        'border-border/70 -mx-3 flex scroll-mt-24 flex-wrap items-start gap-x-4 gap-y-3 border-b px-3 py-3.5 transition-colors last:border-b-0',
        (past || skipped || suspended) && 'opacity-60',
        flashed && 'bg-[#51B0BC]/10',
      )}
    >
      <ParticipationMark week={week} />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium tabular-nums">{formatWeek(week)}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2">
          {suspended || skipped ? (
            <span className="text-muted-foreground text-xs line-through">
              {week.name}
            </span>
          ) : (
            <SprintName week={week} />
          )}
          {note && (
            <span className="text-muted-foreground text-xs">· {note}</span>
          )}
        </p>
      </div>

      {/*
        On a phone this block sits under the dates rather than beside them:
        a cramped right rail made the skip the smallest target on the page,
        and skipping is the one thing this surface asks the client to do.
      */}
      <div className="flex w-full shrink-0 flex-col items-stretch gap-2 text-left sm:w-auto sm:items-end sm:gap-1.5 sm:text-right">
        <WeekAction week={week} />
        {/*
          The move is accepted the instant it is clicked, but the row only
          changes once the server has answered and the calendar refetched.
          A half-opacity disabled button does not read as "working" across
          that gap, so the button says so itself until the new row lands.
        */}
        {week.canUnskip && (
          <Button
            variant="default"
            onClick={onUnskip}
            disabled={pending}
            aria-busy={pending}
            className="h-11 w-full sm:h-8 sm:w-auto"
          >
            {pending ? <Loader2Icon className="animate-spin" /> : <UndoIcon />}
            {pending ? 'Unskipping…' : 'Unskip this week'}
          </Button>
        )}
        {week.canSkip && (
          <Button
            variant="outline"
            onClick={onSkip}
            disabled={pending}
            aria-busy={pending}
            className="h-11 w-full sm:h-8 sm:w-auto"
          >
            {pending && <Loader2Icon className="animate-spin" />}
            {pending ? 'Skipping…' : 'Skip this week'}
          </Button>
        )}
        {moveError && (
          <p role="alert" className="text-destructive max-w-72 text-xs">
            {moveError}
          </p>
        )}
      </div>
    </div>
  )
}

function ReachLine() {
  return (
    <div className="sprint-reach-line flex items-center gap-3 py-3">
      <span className="rounded-md bg-[#51B0BC] px-2 py-0.5 text-xs font-medium text-white">
        Last week you are in
      </span>
      <span className="h-px flex-1 bg-[#51B0BC]" />
      <span className="text-muted-foreground text-xs">
        Everything below needs more Sprints
      </span>
    </div>
  )
}

/**
 * The codes a refused move comes back under: `ClientParticipationError`, and
 * since LG-1073 the `PlanningServiceError` the reconcile raises when the
 * client's plan cannot follow the move. Only these carry a sentence the
 * router wrote for a client to read; any other code -- a BAD_REQUEST holding
 * a serialized zod issue, an internal error holding a Postgres string -- is
 * never quoted.
 */
const REFUSAL_CODES: ReadonlySet<string> = new Set([
  'NOT_FOUND',
  'PRECONDITION_FAILED',
  'CONFLICT',
])

/**
 * The sentence a client reads when their move was refused. The router has
 * already joined `SPRINT_REFUSAL_MESSAGES`' `message` and `followUp` into
 * the error's message, so it is rendered verbatim; anything that is not a
 * refusal gets one generic line rather than a stack trace.
 */
function moveErrorMessage(error: unknown): string {
  if (
    error instanceof TRPCClientError &&
    typeof error.data?.code === 'string' &&
    REFUSAL_CODES.has(error.data.code)
  ) {
    return error.message
  }
  return 'That did not go through. Check your connection and try again.'
}

/** The rail itself, given a loaded calendar. */
export function SprintCalendarView({ calendar }: { calendar: SprintCalendar }) {
  const utils = trpc.useUtils()
  const skipMutation = trpc.dashboard.sprints.skip.useMutation()
  const unskipMutation = trpc.dashboard.sprints.unskip.useMutation()

  const [tab, setTab] = useState<'active' | 'historical'>('active')
  const [flashed, setFlashed] = useState<string | null>(null)
  // A set, not a slot: two rows can be mid-move at once, and a second click
  // elsewhere must not hand the first row its button back early.
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const [moveErrors, setMoveErrors] = useState<Record<string, string>>({})
  const flashTimer = useRef<number | null>(null)
  const rowNodes = useRef(new Map<string, HTMLDivElement>())

  useEffect(
    () => () => {
      if (flashTimer.current !== null) window.clearTimeout(flashTimer.current)
    },
    [],
  )

  // Scrolling waits for the flashed row to be on the Active tab and mounted,
  // which is exactly when this effect runs -- no timer guessing at a render.
  useEffect(() => {
    if (flashed === null) return
    // The CSS opts the card and reach animations out under reduced motion;
    // a smooth scroll is motion too, so it takes the same setting.
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    rowNodes.current.get(flashed)?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center',
    })
  }, [flashed])

  const { active, historical } = splitWeeks(calendar.weeks)
  const shown = tab === 'active' ? active : historical
  const sprintsLeft = calendar.creditBalance
  const runningLow = isRunningLow(sprintsLeft)
  const lastCoveredId = lastCoveredWeekId(calendar.weeks)
  const lastCovered = calendar.weeks.find((w) => w.sprintId === lastCoveredId)
  let lastMonth = ''

  const move = useCallback(
    async (sprintId: string, target: 'skip' | 'unskip') => {
      setPendingIds((prev) => new Set(prev).add(sprintId))
      setMoveErrors((prev) => {
        if (!(sprintId in prev)) return prev
        const { [sprintId]: _cleared, ...rest } = prev
        return rest
      })
      try {
        const mutation = target === 'skip' ? skipMutation : unskipMutation
        await mutation.mutateAsync({ sprintId })
        // A skip moves the reach overlay on every week after it, so nothing
        // narrower than the whole calendar is correct (see
        // DASHBOARD_SPRINTS_INVALIDATION_MAP).
        await utils.dashboard.sprints.calendar.invalidate()
      } catch (error) {
        setMoveErrors((prev) => ({
          ...prev,
          [sprintId]: moveErrorMessage(error),
        }))
        // The calendar may have moved on under the client; show them where
        // it is now rather than the state they clicked on.
        await utils.dashboard.sprints.calendar.invalidate()
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev)
          next.delete(sprintId)
          return next
        })
      }
    },
    [skipMutation, unskipMutation, utils],
  )

  /** Jump to the week a Sprint is booked on, and say so when you land. */
  const jumpTo = (sprintId: string) => {
    setTab('active')
    setFlashed(sprintId)
    if (flashTimer.current !== null) window.clearTimeout(flashTimer.current)
    flashTimer.current = window.setTimeout(() => setFlashed(null), FLASH_MS)
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-start md:gap-6">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
              Your Sprints
              <Hint
                hint={NAMING_BLURB}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <InfoIcon className="size-4" />
                <span className="sr-only">How Sprints are named</span>
              </Hint>
            </h3>
            <p className="text-muted-foreground mt-1 max-w-prose text-sm text-pretty md:max-w-[60%]">
              {sprintsLeft === 0 ? (
                <>
                  Every week the program runs is below. You are in none of them
                  yet — a Sprint puts you in one.
                </>
              ) : (
                <>
                  <span className="text-foreground font-medium tabular-nums">
                    {sprintsLeftLabel(sprintsLeft)}
                  </span>
                  {/* The same promise three times, shortened as the column narrows. */}
                  <span className="hidden sm:inline">
                    , each booked on a week in the list below.{' '}
                  </span>
                  <span className="hidden lg:inline">
                    Got a busy week? No problem — just tell us you will be
                    skipping the Sprint before it locks and you will defer all
                    activity on the program till you get back.
                  </span>
                  <span className="hidden sm:inline lg:hidden">
                    Busy week coming? Tell us before it locks and the whole
                    program waits for you.
                  </span>
                  <span className="sm:hidden">
                    , skip busy weeks before they lock
                  </span>
                </>
              )}
            </p>
          </div>

          {/*
            Standing, not conditional: it changes tone when the balance is
            gone or running low, but never appears or disappears, so the buy
            path is always in the same place -- the top right, unboxed.
          */}
          <aside className="md:w-60 md:text-right">
            <p
              className={cn(
                'text-sm font-medium',
                (sprintsLeft === 0 || runningLow) && 'text-[#51B0BC]',
              )}
            >
              {sprintsLeft === 0
                ? lastCovered
                  ? `You are covered through ${formatWeek(lastCovered)}`
                  : 'You are not in any Sprint'
                : runningLow
                  ? lastCovered
                    ? `The program stops after ${formatWeek(lastCovered)}`
                    : 'Almost out of Sprints'
                  : 'Staying with us longer?'}
            </p>
            <p className="text-muted-foreground mt-1 text-sm">
              {sprintsLeft === 0
                ? lastCovered
                  ? 'Nothing left to spend after that. Buy a Sprint and the weeks after it open up.'
                  : 'Buy a Sprint and you are in next week — homework, coaching, the lot.'
                : runningLow
                  ? `${sprintsLeft === 1 ? 'One Sprint' : `${sprintsLeft} Sprints`} left. Add more and the weeks after it open up.`
                  : 'Add Sprints to reach more weeks below'}
            </p>
            <BuyButton className="mt-3 w-full md:w-auto">
              {sprintsLeft === 0 && !lastCovered
                ? 'Buy your first Sprint'
                : 'Buy more Sprints'}
            </BuyButton>
          </aside>
        </div>

        <div className="mt-4">
          <SprintRondal weeks={calendar.weeks} onJump={jumpTo} />
        </div>
      </div>

      <div className="min-w-0">
        <div
          role="tablist"
          aria-label="Weeks"
          className="border-border mb-1 flex gap-5 border-b"
        >
          {(['active', 'historical'] as const).map((option) => (
            <button
              key={option}
              id={`weeks-tab-${option}`}
              type="button"
              role="tab"
              aria-controls="weeks-panel"
              aria-selected={tab === option}
              onClick={() => setTab(option)}
              className={cn(
                '-mb-px border-b-2 px-0.5 pb-2 text-sm font-medium transition-colors',
                tab === option
                  ? 'border-foreground text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {option === 'active'
                ? `Active (${active.length})`
                : `Historical (${historical.length})`}
            </button>
          ))}
        </div>

        {/* One panel for both tabs: the list below is what a tab switch changes. */}
        <div
          id="weeks-panel"
          role="tabpanel"
          aria-labelledby={`weeks-tab-${tab}`}
        >
          {tab === 'historical' && (
            <p className="text-muted-foreground mt-3 text-sm">
              Weeks that already ran. Nothing here can change.
            </p>
          )}

          {shown.length === 0 && (
            <p className="text-muted-foreground border-border/70 border-t py-8 text-center text-sm">
              {tab === 'historical'
                ? 'Nothing behind you yet — your first Sprint has not run.'
                : 'No weeks ahead. Buy Sprints to put some on the calendar.'}
            </p>
          )}

          {shown.map((week) => {
            const month = formatMonth(week)
            const showMonth = monthKey(week) !== lastMonth
            lastMonth = monthKey(week)
            return (
              <Fragment key={week.sprintId}>
                {showMonth && (
                  <p className="bg-background text-muted-foreground sticky top-16 z-10 py-2 text-xs font-semibold tracking-wide uppercase">
                    {month}
                  </p>
                )}
                <WeekRow
                  ref={(node) => {
                    if (node) rowNodes.current.set(week.sprintId, node)
                    else rowNodes.current.delete(week.sprintId)
                  }}
                  week={week}
                  flashed={flashed === week.sprintId}
                  pending={pendingIds.has(week.sprintId)}
                  moveError={moveErrors[week.sprintId] ?? null}
                  onSkip={() => void move(week.sprintId, 'skip')}
                  onUnskip={() => void move(week.sprintId, 'unskip')}
                />
                {tab === 'active' && lastCoveredId === week.sprintId && (
                  // Keyed on the week it sits under so the line re-mounts, and
                  // re-animates, each time a skip moves it.
                  <Fragment key={`reach-${lastCoveredId}`}>
                    <ReachLine />
                    {runningLow && (
                      <div className="border-border/70 flex flex-wrap items-center justify-between gap-3 border-b py-3">
                        <p className="text-sm">
                          That is the end of what you have bought. Keep the
                          program running past it?
                        </p>
                        <BuyButton size="sm">Buy more Sprints</BuyButton>
                      </div>
                    )}
                  </Fragment>
                )}
              </Fragment>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function CalendarSkeleton() {
  return (
    <div
      role="status"
      className="space-y-8"
      aria-busy="true"
      aria-label="Loading Sprints"
    >
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:gap-6">
        <div className="space-y-3">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-4 w-full max-w-prose" />
        </div>
        <Skeleton className="h-32 md:w-60" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-20 w-40" />
        <Skeleton className="h-20 w-40" />
        <Skeleton className="h-20 w-40" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    </div>
  )
}

/**
 * A signed-in client with no active engagement gets an empty calendar rather
 * than an error. There are no weeks to show and no Sprint to buy into yet, so
 * the page says what a Sprint is and stops.
 */
function NoEngagement() {
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold tracking-tight">Your Sprints</h3>
      <p className="text-muted-foreground max-w-prose text-sm">
        Sprints are the weeks of coaching you hold. You do not have an active
        coaching engagement yet, so there is nothing on the calendar — once you
        are in the program, every week ahead shows up here.
      </p>
    </div>
  )
}

/**
 * The Sprint management section of Settings: query, then the rail. The
 * settings shell owns the page frame, the "Sprint management" heading and the
 * document title; this is the body under them.
 */
export function SprintCalendarSection() {
  const query = trpc.dashboard.sprints.calendar.useQuery()

  if (query.isLoading) return <CalendarSkeleton />
  if (query.isError || !query.data) {
    return (
      <p role="alert" className="text-muted-foreground text-sm">
        Could not load your Sprints. Reload the page to try again.
      </p>
    )
  }
  if (!query.data.hasEngagement) return <NoEngagement />
  return <SprintCalendarView calendar={query.data} />
}
